import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface PlatformErrorBoundaryProps {
  children: ReactNode;
}

interface PlatformErrorBoundaryState {
  hasError: boolean;
}

export class PlatformErrorBoundary extends Component<
  PlatformErrorBoundaryProps,
  PlatformErrorBoundaryState
> {
  state: PlatformErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PlatformErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[PlatformErrorBoundary]", error, info.componentStack);
    fetch("/bff/error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 1000) ?? null,
        url: window.location.href,
      }),
    }).catch(() => {});
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Stack
        component="main"
        spacing={2}
        sx={{
          minHeight: "100dvh",
          px: 3,
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography component="h1" variant="h4">
          Something went wrong
        </Typography>
        <Typography color="text.secondary">
          An unexpected error occurred. Reload the page to try again.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Stack>
    );
  }
}
